import { useReducer } from 'react';
import type {
    PageComponentValidationAPI,
    PageComponentValidationType,
} from '../../../../../../../../types/validation';
import type { ContentType, ValueElementIdReferenceAPI } from '../../../../../../../../types/value';
import ButtonDefault from '../../../../../../../buttons/ButtonDefault';
import ButtonPrimary from '../../../../../../../buttons/ButtonPrimary';
import FormGroup from '../../../../../../../generic/FormGroup';
import { COMPONENT_CONFIGURATION_LABELS } from '../../../../../../constants';
import { usePageEditor } from '../../../../../PageEditorProvider';
import DateTimeInputWithValuePicker from '../general-config/DateTimeInputWithValuePicker';
import InputWithValuePicker from '../general-config/InputWithValuePicker';

interface Props {
    contentType: Readonly<ContentType | null>;
    validation: Readonly<PageComponentValidationAPI | null>;
    onSave: (validation: PageComponentValidationAPI) => void;
    onCancel: () => void;
}

interface State {
    validationToEdit: PageComponentValidationAPI;
    hasSubmitted: boolean;
}

type Action =
    | {
          type: 'updateType';
          payload: { type: PageComponentValidationType };
      }
    | {
          type: 'updateValue';
          payload: { value: string | number | ValueElementIdReferenceAPI | null };
      }
    | {
          type: 'updateMessage';
          payload: { message: string };
      }
    | {
          type: 'submit';
      };

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'updateType':
            return {
                ...state,
                validationToEdit: {
                    ...state.validationToEdit,
                    type: action.payload.type,
                },
            };
        case 'updateValue': {
            return {
                ...state,
                validationToEdit: {
                    ...state.validationToEdit,
                    value: action.payload.value,
                },
            };
        }
        case 'updateMessage':
            return {
                ...state,
                validationToEdit: {
                    ...state.validationToEdit,
                    message: action.payload.message,
                },
            };
        case 'submit':
            return {
                ...state,
                hasSubmitted: true,
            };
    }
};

const ValidationForm = ({ contentType, validation, onSave, onCancel }: Props) => {
    const { container } = usePageEditor();

    const getApplicableValidationOptions = (
        contentType: ContentType | null,
    ): PageComponentValidationType[] => {
        switch (contentType) {
            case null:
            case 'ContentBoolean':
            case 'ContentObject':
                return [];

            case 'ContentString':
            case 'ContentPassword':
            case 'ContentEncrypted':
            case 'ContentContent':
            case 'ContentCode':
                return ['Pattern', 'Minimum', 'Maximum'];

            case 'ContentDate':
            case 'ContentDateTime':
                return ['Minimum', 'Maximum'];

            case 'ContentNumber':
            case 'ContentList':
                return ['Minimum', 'Maximum'];
        }
    };

    const getInputType = (
        contentType: ContentType,
        validationType: PageComponentValidationType,
    ): 'text' | 'date' | 'datetime-local' | 'number' => {
        switch (contentType) {
            case null:
            case 'ContentBoolean':
            case 'ContentObject':
            case 'ContentString':
            case 'ContentPassword':
            case 'ContentEncrypted':
            case 'ContentContent':
            case 'ContentCode': {
                if (validationType === 'Pattern') {
                    return 'text';
                }
                return 'number';
            }

            case 'ContentDate':
                return 'date';

            case 'ContentDateTime':
                return 'datetime-local';

            case 'ContentNumber':
            case 'ContentList':
                return 'number';
        }
    };

    const getValidationLabel = (
        contentType: ContentType,
        validationType: PageComponentValidationType,
    ) => {
        switch (validationType) {
            case 'Pattern':
                return COMPONENT_CONFIGURATION_LABELS['VALIDATION_PATTERN'];

            case 'Minimum':
                return contentType === 'ContentDate'
                    ? COMPONENT_CONFIGURATION_LABELS['VALIDATION_MIN_DATE']
                    : contentType === 'ContentDateTime'
                      ? COMPONENT_CONFIGURATION_LABELS['VALIDATION_MIN_DATE_TIME']
                      : COMPONENT_CONFIGURATION_LABELS['VALIDATION_MIN_NUMBER'];

            case 'Maximum':
                return contentType === 'ContentDate'
                    ? COMPONENT_CONFIGURATION_LABELS['VALIDATION_MAX_DATE']
                    : contentType === 'ContentDateTime'
                      ? COMPONENT_CONFIGURATION_LABELS['VALIDATION_MAX_DATE_TIME']
                      : COMPONENT_CONFIGURATION_LABELS['VALIDATION_MAX_NUMBER'];
        }
    };

    const createNewValidation = (contentType: ContentType | null): PageComponentValidationAPI => {
        switch (contentType) {
            case null:
            case 'ContentBoolean':
            case 'ContentObject':
            case 'ContentString':
            case 'ContentPassword':
            case 'ContentEncrypted':
            case 'ContentContent':
            case 'ContentCode':
                return { type: 'Pattern', value: '', message: '' };

            case 'ContentDate':
            case 'ContentDateTime':
            case 'ContentNumber':
            case 'ContentList':
                return { type: 'Minimum', value: '', message: '' };
        }
    };

    const onSubmit = () => {
        if (isValid) {
            onSave(state.validationToEdit);
        } else {
            dispatch({ type: 'submit' });
        }
    };

    const isNew = validation === null;

    const initialValidationToEdit: PageComponentValidationAPI = isNew
        ? createNewValidation(contentType)
        : validation;

    const initialState: State = {
        validationToEdit: initialValidationToEdit,
        hasSubmitted: false,
    };

    const [state, dispatch] = useReducer(reducer, initialState);

    const valueIsValid =
        state.validationToEdit.value !== null &&
        state.validationToEdit.value !== undefined &&
        state.validationToEdit.value !== '';

    const messageIsValid = state.validationToEdit.message !== '';

    const isValid = valueIsValid && messageIsValid;

    if (contentType === null) {
        // The form makes no sense without a contentType.
        // This shouldn't happen, but just in case.
        return null;
    }

    const inputType = getInputType(contentType, state.validationToEdit.type);

    const handleValueChange = (value: string | ValueElementIdReferenceAPI | null) => {
        const shouldConvertToNumber =
            inputType === 'number' &&
            typeof value === 'string' &&
            value !== '' &&
            !Number.isNaN(Number(value));

        dispatch({
            type: 'updateValue',
            payload: {
                value: shouldConvertToNumber ? Number(value) : value,
            },
        });
    };

    return (
        <div className="sidebar-mini-editor">
            <h4 className="sidebar-section-heading">{`${
                isNew ? 'Create new ' : 'Edit '
            } validation`}</h4>
            <FormGroup label="Type" htmlFor="validation-type" isRequired>
                <select
                    id="validation-type"
                    className="form-control validation-type"
                    value={state.validationToEdit.type}
                    title="Type of validation"
                    onChange={({ target: { value } }) =>
                        dispatch({
                            type: 'updateType',
                            payload: { type: value as PageComponentValidationType },
                        })
                    }
                >
                    {getApplicableValidationOptions(contentType).map((validationType) => (
                        <option key={validationType} value={validationType}>
                            {getValidationLabel(contentType, validationType)}
                        </option>
                    ))}
                </select>
            </FormGroup>
            {inputType === 'datetime-local' ? (
                <DateTimeInputWithValuePicker
                    id="validation-value"
                    label="Value"
                    isRequired
                    testId="validation-value-configuration"
                    value={
                        state.validationToEdit.value as
                            | string
                            | ValueElementIdReferenceAPI
                            | undefined
                    }
                    onChangeAsValueReference={handleValueChange}
                    isUTC
                    container={container}
                />
            ) : (
                <InputWithValuePicker
                    id="validation-value"
                    label="Value"
                    isRequired
                    testId="validation-value-configuration"
                    value={
                        state.validationToEdit.value as
                            | string
                            | ValueElementIdReferenceAPI
                            | undefined
                    }
                    onChangeAsValueReference={handleValueChange}
                    type={inputType}
                    title="Validation value"
                    container={container}
                />
            )}
            <FormGroup
                isRequired
                label="Message"
                htmlFor="validation-message"
                showValidation={!messageIsValid && state.hasSubmitted}
                validationMessage="Message is required"
            >
                <input
                    id="validation-message"
                    title="Validation message"
                    value={state.validationToEdit.message}
                    onChange={({ target: { value } }) =>
                        dispatch({ type: 'updateMessage', payload: { message: value } })
                    }
                    className="form-control"
                    type="text"
                />
            </FormGroup>
            <div className="margin-top">
                <ButtonDefault onClick={onCancel}>Cancel</ButtonDefault>
                <ButtonPrimary onClick={onSubmit}>{isNew ? 'Add' : 'Update'}</ButtonPrimary>
            </div>
        </div>
    );
};

export default ValidationForm;
